import React, { useState, Children } from 'react';
import { ArrowLeftIcon, XIcon, AlertCircleIcon, TrashIcon } from 'lucide-react';
interface EditRoomFormProps {
  roomData: any;
  onSave: (roomData: any) => void;
  onCancel: () => void;
  onNavigateToAssets: () => void;
}
export function EditRoomForm({
  roomData: initialRoomData,
  onSave,
  onCancel,
  onNavigateToAssets
}: EditRoomFormProps) {
  const [roomData, setRoomData] = useState(initialRoomData);
  const [activeTab, setActiveTab] = useState<'info' | 'media'>('info');
  const roomCategories = ['SUPERIOR', 'DELUXE', 'OVERWATER_VILLA', 'BEACH_VILLA', 'SUITE'];
  const roomViews = ['SEA_VIEW', 'GARDEN_VIEW', 'OCEAN_VIEW', 'LAGOON_VIEW', 'BEACH_VIEW'];
  const bedTypes = ['King', 'Queen', 'Double', 'Twin', 'Single', 'Sofa Bed', 'Bunk Bed'];
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setRoomData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      checked
    } = e.target;
    setRoomData(prev => ({
      ...prev,
      [name]: checked
    }));
  };
  const handleNumberChange = (name: string, value: string) => {
    const numValue = value === '' ? 0 : parseInt(value, 10);
    setRoomData(prev => ({
      ...prev,
      [name]: numValue
    }));
  };
  const handleBedConfigChange = (index: number, field: string, value: string) => {
    const updatedConfigs = [...roomData.bedConfigurations];
    if (field === 'count') {
      updatedConfigs[index] = {
        ...updatedConfigs[index],
        [field]: parseInt(value, 10) || 1
      };
    } else {
      updatedConfigs[index] = {
        ...updatedConfigs[index],
        [field]: value
      };
    }
    setRoomData(prev => ({
      ...prev,
      bedConfigurations: updatedConfigs
    }));
  };
  const removeBedConfig = (index: number) => {
    const updatedConfigs = [...roomData.bedConfigurations];
    updatedConfigs.splice(index, 1);
    setRoomData(prev => ({
      ...prev,
      bedConfigurations: updatedConfigs
    }));
  };
  const handleRemoveAmenity = (amenity: string) => {
    setRoomData(prev => ({
      ...prev,
      amenities: prev.amenities.filter((a: string) => a !== amenity)
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(roomData);
  };
  return <div className="space-y-6" data-id="element-1802">
      <div className="flex items-center" data-id="element-1803">
        <button onClick={onCancel} className="flex items-center text-gray-600 hover:text-gray-900" data-id="element-1804">
          <ArrowLeftIcon size={18} className="mr-1" data-id="element-1805" />
          <span data-id="element-1806">Edit Room</span>
        </button>
      </div>
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-1807">
        {/* Tabs */}
        <div className="border-b border-gray-200" data-id="element-1808">
          <nav className="flex" data-id="element-1809">
            <button className={`px-6 py-4 text-sm font-medium ${activeTab === 'info' ? 'border-b-2 border-red-500 text-red-600' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('info')} data-id="element-1810">
              Room information
            </button>
            <button className={`px-6 py-4 text-sm font-medium ${activeTab === 'media' ? 'border-b-2 border-red-500 text-red-600' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={onNavigateToAssets} data-id="element-1811">
              Media assets
            </button>
          </nav>
        </div>
        {/* Form Content */}
        <form onSubmit={handleSubmit} className="p-6" data-id="element-1812">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-1813">
            <div data-id="element-1814">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1815">
                Room name<span className="text-red-500" data-id="element-1816">*</span>
              </label>
              <input type="text" id="name" name="name" value={roomData.name} onChange={handleInputChange} required className="w-full p-2 border border-gray-300 rounded-md" data-id="element-1817" />
            </div>
            <div data-id="element-1818">
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1819">
                Room category<span className="text-red-500" data-id="element-1820">*</span>
              </label>
              <div className="relative" data-id="element-1821">
                <div className="flex items-center" data-id="element-1822">
                  <input type="text" value={roomData.category} readOnly className="w-full p-2 border border-gray-300 rounded-md bg-gray-50" data-id="element-1823" />
                  <button type="button" className="absolute right-2 text-gray-400 hover:text-gray-600" onClick={() => setRoomData(prev => ({
                  ...prev,
                  category: ''
                }))} data-id="element-1824">
                    <XIcon size={16} data-id="element-1825" />
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="mb-6" data-id="element-1826">
            <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1827">
              Room code
            </label>
            <input type="text" id="code" name="code" value={roomData.code} onChange={handleInputChange} className="w-full p-2 border border-gray-300 rounded-md" data-id="element-1828" />
          </div>
          <div className="mb-6" data-id="element-1829">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1830">
              Description
            </label>
            <textarea id="description" name="description" value={roomData.description} onChange={handleInputChange} rows={4} className="w-full p-2 border border-gray-300 rounded-md" data-id="element-1831" />
          </div>
          <div className="flex flex-wrap gap-6 mb-6" data-id="element-1832">
            <div className="flex items-center" data-id="element-1833">
              <input type="checkbox" id="isSmoking" name="isSmoking" checked={roomData.isSmoking} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-1834" />
              <label htmlFor="isSmoking" className="ml-2 text-sm text-gray-700" data-id="element-1835">
                Smoking
              </label>
            </div>
            <div className="flex items-center" data-id="element-1836">
              <input type="checkbox" id="isAccessible" name="isAccessible" checked={roomData.isAccessible} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-1837" />
              <label htmlFor="isAccessible" className="ml-2 text-sm text-gray-700" data-id="element-1838">
                Accessibility
              </label>
            </div>
            <div className="flex items-center" data-id="element-1839">
              <input type="checkbox" id="isActive" name="isActive" checked={roomData.isActive} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-1840" />
              <label htmlFor="isActive" className="ml-2 text-sm text-gray-700" data-id="element-1841">
                Status
              </label>
            </div>
          </div>
          {/* Room View and Size */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-1842">
            <div data-id="element-1843">
              <label htmlFor="view" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1844">
                Room view<span className="text-red-500" data-id="element-1845">*</span>
              </label>
              <div className="relative" data-id="element-1846">
                <div className="flex items-center" data-id="element-1847">
                  <input type="text" value={roomData.view} readOnly className="w-full p-2 border border-gray-300 rounded-md bg-gray-50" data-id="element-1848" />
                  <button type="button" className="absolute right-2 text-gray-400 hover:text-gray-600" onClick={() => setRoomData(prev => ({
                  ...prev,
                  view: ''
                }))} data-id="element-1849">
                    <XIcon size={16} data-id="element-1850" />
                  </button>
                </div>
              </div>
            </div>
            <div data-id="element-1851">
              <label htmlFor="size" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1852">
                Room size (sq m)
              </label>
              <input type="number" id="size" name="size" value={roomData.size} onChange={handleInputChange} min="0" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-1853" />
            </div>
          </div>
          {/* Room Amenities */}
          <div className="mb-6" data-id="element-1854">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1855">
              Room amenities
            </label>
            <div className="flex flex-wrap gap-2" data-id="element-1856">
              {roomData.amenities.map((amenity: string) => <div key={amenity} className="flex items-center bg-gray-100 rounded-md px-2 py-1" data-id="element-1857">
                  <span className="text-sm" data-id="element-1858">{amenity}</span>
                  <button type="button" onClick={() => handleRemoveAmenity(amenity)} className="ml-1 text-gray-400 hover:text-gray-600" data-id="element-1859">
                    <XIcon size={12} data-id="element-1860" />
                  </button>
                </div>)}
            </div>
          </div>
          {/* Occupancy Settings */}
          <div className="mb-6" data-id="element-1861">
            <h3 className="text-md font-medium text-gray-800 mb-4" data-id="element-1862">
              Occupancy Settings
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4" data-id="element-1863">
              <div data-id="element-1864">
                <label htmlFor="baseAdultsOccupancy" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1865">
                  Base Adults Occupancy<span className="text-red-500" data-id="element-1866">*</span>
                </label>
                <input type="number" id="baseAdultsOccupancy" value={roomData.baseAdultsOccupancy} onChange={e => handleNumberChange('baseAdultsOccupancy', e.target.value)} min="1" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-1867" />
                <p className="text-xs text-gray-500 mt-1" data-id="element-1868">
                  Standard number of allowed adults for this room
                </p>
              </div>
              <div data-id="element-1869">
                <label htmlFor="baseChildrenOccupancy" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1870">
                  Base Children Occupancy<span className="text-red-500" data-id="element-1871">*</span>
                </label>
                <input type="number" id="baseChildrenOccupancy" value={roomData.baseChildrenOccupancy} onChange={e => handleNumberChange('baseChildrenOccupancy', e.target.value)} min="0" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-1872" />
                <p className="text-xs text-gray-500 mt-1" data-id="element-1873">
                  Standard number of allowed children for this room
                </p>
              </div>
              <div data-id="element-1874">
                <label htmlFor="maxOccupancy" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1875">
                  Maximum Occupancy<span className="text-red-500" data-id="element-1876">*</span>
                </label>
                <input type="number" id="maxOccupancy" value={roomData.maxOccupancy} onChange={e => handleNumberChange('maxOccupancy', e.target.value)} min="1" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-1877" />
                <p className="text-xs text-gray-500 mt-1" data-id="element-1878">
                  Maximum number of guests allowed
                </p>
              </div>
            </div>
            <div className="space-y-2 mb-4" data-id="element-1879">
              <div className="flex items-center" data-id="element-1880">
                <input type="checkbox" id="allowAdditionalAdults" name="allowAdditionalAdults" checked={roomData.allowAdditionalAdults} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-1881" />
                <label htmlFor="allowAdditionalAdults" className="ml-2 text-sm text-gray-700" data-id="element-1882">
                  Allow additional adults
                </label>
              </div>
              <p className="text-xs text-gray-500 ml-6" data-id="element-1883">
                Allow additional adults (not exceeding total occupancy limit)
              </p>
              <div className="flex items-center mt-2" data-id="element-1884">
                <input type="checkbox" id="allowAdditionalChildren" name="allowAdditionalChildren" checked={roomData.allowAdditionalChildren} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-1885" />
                <label htmlFor="allowAdditionalChildren" className="ml-2 text-sm text-gray-700" data-id="element-1886">
                  Allow additional children
                </label>
              </div>
              <p className="text-xs text-gray-500 ml-6" data-id="element-1887">
                Allow additional children (not exceeding total occupancy limit)
              </p>
            </div>
            {/* Occupancy Summary */}
            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4" data-id="element-1888">
              <div className="flex" data-id="element-1889">
                <AlertCircleIcon className="h-5 w-5 text-red-500 mr-2" data-id="element-1890" />
                <div data-id="element-1891">
                  <p className="text-sm text-red-700" data-id="element-1892">
                    This room has a base occupancy of{' '}
                    {roomData.baseAdultsOccupancy} Adults and{' '}
                    {roomData.baseChildrenOccupancy} Children with a total
                    maximum occupancy of {roomData.maxOccupancy}.
                  </p>
                  <p className="text-sm text-red-700" data-id="element-1893">
                    Additional adults are not allowed.
                  </p>
                  <p className="text-sm text-red-700" data-id="element-1894">
                    Additional children are not allowed.
                  </p>
                  <p className="text-sm text-red-700" data-id="element-1895">
                    Additional cost per child or adults on top of the base
                    occupancy can be configured in rates.
                  </p>
                </div>
              </div>
            </div>
          </div>
          {/* Bed Configuration */}
          <div className="mb-6" data-id="element-1896">
            <h3 className="text-md font-medium text-gray-800 mb-4" data-id="element-1897">
              Bed configuration<span className="text-red-500" data-id="element-1898">*</span>
            </h3>
            {roomData.bedConfigurations.map((config: any, index: number) => <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 items-end" data-id="element-1899">
                <div data-id="element-1900">
                  <label htmlFor={`bedType-${index}`} className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1901">
                    Bed type
                  </label>
                  <div className="relative" data-id="element-1902">
                    <select id={`bedType-${index}`} value={config.type} onChange={e => handleBedConfigChange(index, 'type', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md appearance-none pr-8" data-id="element-1903">
                      <option value="" data-id="element-1904">Select bed type</option>
                      {bedTypes.map(type => <option key={type} value={type} data-id="element-1905">
                          {type}
                        </option>)}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none" data-id="element-1906">
                      <svg className="w-4 h-4 fill-current text-gray-500" viewBox="0 0 20 20" data-id="element-1907">
                        <path d="M7 7l3-3 3 3m0 6l-3 3-3-3" strokeWidth="1.5" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round" data-id="element-1908" />
                      </svg>
                    </div>
                  </div>
                </div>
                <div className="flex items-center" data-id="element-1909">
                  <div className="flex-grow" data-id="element-1910">
                    <label htmlFor={`bedCount-${index}`} className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1911">
                      Bed count
                    </label>
                    <input type="number" id={`bedCount-${index}`} value={config.count} onChange={e => handleBedConfigChange(index, 'count', e.target.value)} min="1" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-1912" />
                  </div>
                  {roomData.bedConfigurations.length > 1 && <button type="button" onClick={() => removeBedConfig(index)} className="ml-2 mt-6 p-2 text-red-600 hover:text-red-800" data-id="element-1913">
                      <TrashIcon size={18} data-id="element-1914" />
                    </button>}
                </div>
              </div>)}
          </div>
          {/* Submit Button */}
          <div className="flex justify-end" data-id="element-1915">
            <button type="submit" className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700" data-id="element-1916">
              Save
            </button>
          </div>
        </form>
      </div>
    </div>;
}